import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { FiShoppingCart, FiArrowRight } from "react-icons/fi";

import { fetchShopProductBatch, fetchShopSklads } from "../api";
import { useCart } from "../cart";
import { buildAccent, buildFallbackLabel, formatPrice } from "../utils";
import { STORE_CATALOG_PATH, storeProductPath } from "../../routes";
import useAutoLoadMore from "../useAutoLoadMore";

const RAIL_PAGE = 10;
const GRID_PAGE = 20;

/* ─── maps helper ─── */
function toMapsUrl(v) {
  if (!v) return null;
  const s = v.trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s)}`;
}

/* ─── Kichik mahsulot kartasi (rail uchun) ─── */
function RailCard({ product, index }) {
  const accent = buildAccent(index);
  const { addItem } = useCart();
  const navigate = useNavigate();
  const name = String(product?.name || product?.slug || "Nomsiz").trim();

  return (
    <article
      className={`sh-rail-card store-card-${accent}`}
      onClick={() => navigate(storeProductPath(product.slug))}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(storeProductPath(product.slug))}
    >
      <div className="sh-rail-media">
        {product.primary_image
          ? <img src={product.primary_image} alt={name} loading="lazy" />
          : <div className="sh-rail-fallback">{buildFallbackLabel(name)}</div>
        }
      </div>
      <div className="sh-rail-body">
        <p className="sh-rail-name">{name}</p>
        <div className="sh-rail-foot">
          <strong className="sh-rail-price">{formatPrice(product.price)}</strong>
          <button
            className="sh-rail-btn"
            onClick={(e) => { e.stopPropagation(); addItem(product, 1); }}
            aria-label="Savatga qo'shish"
          >
            <FiShoppingCart size={14} />
          </button>
        </div>
      </div>
    </article>
  );
}

/* ─── Katta mahsulot kartasi (grid uchun) ─── */
function GridCard({ product, index }) {
  const accent = buildAccent(index);
  const { addItem } = useCart();
  const navigate = useNavigate();
  const name = String(product?.name || product?.slug || "Nomsiz").trim();

  return (
    <article
      className={`sh-grid-card store-card-${accent}`}
      onClick={() => navigate(storeProductPath(product.slug))}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(storeProductPath(product.slug))}
    >
      <div className="sh-grid-media">
        {product.primary_image
          ? <img src={product.primary_image} alt={name} loading="lazy" />
          : <div className="sh-grid-fallback">{buildFallbackLabel(name)}</div>
        }
      </div>
      <div className="sh-grid-body">
        <p className="sh-grid-name">{name}</p>
        <div className="sh-grid-foot">
          <strong className="sh-grid-price">{formatPrice(product.price)}</strong>
          <button
            className="sh-grid-btn"
            onClick={(e) => { e.stopPropagation(); addItem(product, 1); }}
            aria-label="Savatga qo'shish"
          >
            <FiShoppingCart size={15} />
          </button>
        </div>
      </div>
    </article>
  );
}

/* ─── Gorizontal Rail ─── */
function ProductRail({ title, subtitle, sort, sklad, catalogLink }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const railRef = useRef(null);
  const sentinelRef = useRef(null);
  const offsetRef = useRef(0);
  const busyRef = useRef(false);

  const load = useCallback(async (offset = 0, append = false) => {
    if (busyRef.current) return;
    busyRef.current = true;
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const { items: batch, hasMore: more } = await fetchShopProductBatch({
        sort, sklad: sklad || undefined, limit: RAIL_PAGE + 1, offset,
      });
      offsetRef.current = offset + batch.length;
      setHasMore(more);
      if (append) setItems(p => [...p, ...batch]);
      else setItems(batch);
    } catch {
      /* ignore */
    } finally {
      busyRef.current = false;
      if (!append) setLoading(false); else setLoadingMore(false);
    }
  }, [sort, sklad]);

  useEffect(() => {
    offsetRef.current = 0;
    load(0, false);
  }, [load]);

  /* Rail oxiriga yetganda keyingini yuklash */
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !busyRef.current) {
          load(offsetRef.current, true);
        }
      },
      { root: railRef.current, threshold: 0.1 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, load]);

  return (
    <section className="sh-section">
      <div className="sh-section-head">
        <div>
          <p className="sh-kicker">{subtitle}</p>
          <h2 className="sh-title">{title}</h2>
        </div>
        <Link className="sh-see-all" to={catalogLink}>
          Barchasini ko'rish <FiArrowRight size={14} />
        </Link>
      </div>

      {loading ? (
        <div className="sh-rail-wrap">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="sh-rail-card sh-skeleton" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="sh-empty">Mahsulot topilmadi.</p>
      ) : (
        <div className="sh-rail-outer" ref={railRef}>
          {items.map((p, i) => <RailCard key={p.id} product={p} index={i} />)}
          {loadingMore && <div className="sh-rail-card sh-skeleton" />}
          {hasMore && <div ref={sentinelRef} className="sh-rail-sentinel" />}
        </div>
      )}
    </section>
  );
}

/* ─── Vertikal mahsulotlar gridi ─── */
function ProductGrid({ sklad, search }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const busyRef = useRef(false);

  const load = useCallback(async (offset = 0, append = false) => {
    if (busyRef.current) return;
    busyRef.current = true;
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const { items: batch, hasMore: more } = await fetchShopProductBatch({
        sort: "newest", sklad: sklad || undefined,
        search: search || undefined,
        limit: GRID_PAGE + 1, offset,
      });
      offsetRef.current = offset + batch.length;
      setHasMore(more);
      if (append) setItems(p => [...p, ...batch]);
      else setItems(batch);
    } catch {
      /* ignore */
    } finally {
      busyRef.current = false;
      if (!append) setLoading(false); else setLoadingMore(false);
    }
  }, [sklad, search]);

  useEffect(() => {
    offsetRef.current = 0;
    load(0, false);
  }, [load]);

  const handleLoadMore = useCallback(() => {
    load(offsetRef.current, true);
  }, [load]);

  const loadMoreRef = useAutoLoadMore({
    hasMore,
    isLoading: loading || loadingMore,
    onLoadMore: handleLoadMore,
  });

  return (
    <section className="sh-section">
      <div className="sh-section-head">
        <div>
          <p className="sh-kicker">Barcha mahsulotlar</p>
          <h2 className="sh-title">Katalog</h2>
        </div>
        <Link className="sh-see-all" to={STORE_CATALOG_PATH}>
          To'liq katalog <FiArrowRight size={14} />
        </Link>
      </div>

      {loading ? (
        <div className="sh-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="sh-grid-card sh-skeleton" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="sh-empty">Mahsulot topilmadi.</p>
      ) : (
        <>
          <div className="sh-grid">
            {items.map((p, i) => <GridCard key={p.id} product={p} index={i} />)}
          </div>
          {loadingMore && (
            <div className="sh-grid sh-grid-more">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="sh-grid-card sh-skeleton" />
              ))}
            </div>
          )}
          {hasMore && <div ref={loadMoreRef} className="sh-scroll-sentinel" />}
        </>
      )}
    </section>
  );
}

/* ─── Do'kon info chiplari ─── */
function StoreChips({ sklad, onClear }) {
  return (
    <div className="store-chips-row store-reveal">
      <button className="store-chip store-chip-name" onClick={onClear}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        {sklad.nom}
        <span className="store-chip-close">×</span>
      </button>
      {sklad.telefon && (
        <a href={`tel:${sklad.telefon}`} className="store-chip store-chip-phone">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012.18 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.72 6.72l1.28-1.28a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
          </svg>
          {sklad.telefon}
        </a>
      )}
      {sklad.location && (
        <div className="store-chip store-chip-location">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
          </svg>
          {sklad.location}
        </div>
      )}
      {sklad.maps_link && (
        <a href={toMapsUrl(sklad.maps_link)} target="_blank" rel="noopener noreferrer" className="store-chip store-chip-maps">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="3 11 22 2 13 21 11 13 3 11"/>
          </svg>
          Xaritada ko'rish
        </a>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   ASOSIY SAHIFA
═══════════════════════════════════════ */
export default function StoreHomePage() {
  const { shopSearch, setShopSearch } = useOutletContext() || {};

  const [sklads, setSklads] = useState([]);
  const [skladsLoading, setSkladsLoading] = useState(true);
  const [activeSkladId, setActiveSkladId] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  /* Debounce product search */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(productSearch.trim()), 400);
    return () => clearTimeout(t);
  }, [productSearch]);

  useEffect(() => {
    fetchShopSklads().then(setSklads).catch(() => setSklads([])).finally(() => setSkladsLoading(false));
  }, []);

  const filteredSklads = useMemo(() => {
    const q = (shopSearch || "").trim().toLowerCase();
    if (!q) return sklads;
    return sklads.filter(s => s.nom.toLowerCase().includes(q));
  }, [sklads, shopSearch]);

  const activeSkladInfo = useMemo(
    () => activeSkladId ? sklads.find(s => s.id === activeSkladId) : null,
    [activeSkladId, sklads]
  );

  const catalogLink = activeSkladId
    ? `${STORE_CATALOG_PATH}?sklad=${activeSkladId}`
    : STORE_CATALOG_PATH;

  return (
    <main className="sh-page">

      {/* ── DO'KONLAR ── */}
      <section className="sh-shops store-reveal">
        {skladsLoading ? (
          <div className="sh-shops-row">
            {[1,2,3,4].map(i => <div key={i} className="sh-shop-card sh-skeleton-circle" />)}
          </div>
        ) : (
          <div className="sh-shops-row">
            {/* Barchasi */}
            <button
              className={"sh-shop-card" + (!activeSkladId ? " active" : "")}
              onClick={() => setActiveSkladId(null)}
            >
              <div className="sh-shop-avatar sh-shop-all"><span>🏪</span></div>
              <span className="sh-shop-name">Barchasi</span>
            </button>

            {filteredSklads.map(s => (
              <button
                key={s.id}
                className={"sh-shop-card" + (activeSkladId === s.id ? " active" : "")}
                onClick={() => { setActiveSkladId(s.id); setShopSearch?.(""); }}
              >
                <div className="sh-shop-avatar">
                  {s.rasm
                    ? <img src={s.rasm} alt={s.nom} />
                    : <span className="sh-shop-letter">{s.nom?.[0]?.toUpperCase() || "?"}</span>
                  }
                </div>
                <span className="sh-shop-name">{s.nom}</span>
              </button>
            ))}
          </div>
        )}

        {activeSkladInfo && (
          <StoreChips
            key={activeSkladInfo.id}
            sklad={activeSkladInfo}
            onClear={() => setActiveSkladId(null)}
          />
        )}
      </section>

      {/* ── ENG KO'P SOTILGAN ── */}
      <ProductRail
        title="Eng ko'p sotilgan"
        subtitle="Bestsellers"
        sort="bestsellers"
        sklad={activeSkladId}
        catalogLink={`${catalogLink}${catalogLink.includes("?") ? "&" : "?"}sort=bestsellers`}
      />

      {/* ── ENG SO'NGGI QO'SHILGAN ── */}
      <ProductRail
        title="Yangi kelganlar"
        subtitle="Yangiliklar"
        sort="newest"
        sklad={activeSkladId}
        catalogLink={`${catalogLink}${catalogLink.includes("?") ? "&" : "?"}sort=newest`}
      />

      {/* ── BARCHA MAHSULOTLAR ── */}
      <ProductGrid sklad={activeSkladId} search={debouncedSearch} />

    </main>
  );
}
